import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card } from 'antd';
import { round } from '@turf/turf';
import { parseISODate } from '../utils';

Highcharts.setOptions({
  time: {
    useUTC: false
  },
  lang: {
    months: [
      'Janvier', 'Février', 'Mars', 'Avril',
      'Mai', 'Juin', 'Juillet', 'Août',
      'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ],
    weekdays: [
      'Dimanche', 'Lundi', 'Mardi', 'Mercredi',
      'Jeudi', 'Vendredi', 'Samedi'
    ],
    shortMonths: [
      'Jan', 'Fév', 'Mar', 'Avr',
      'Mai', 'Juin', 'Juil', 'Août',
      'Sep', 'Oct', 'Nov', 'Déc'
    ],
    decimalPoint: ',',
    thousandsSep: ' '
  },
  credits: {
    enabled: false
  }
});

const IrrigationChart = ({ data, hpfp_percent, hcc_percent }) => {
  const chartRef = useRef(null);

  data.sort((a, b) => parseISODate(a.reception_date) - parseISODate(b.reception_date));

  let hum_data_yaxis_min = Math.min(...data.map(d => {
    const hum1 = Number(d.hum_1);
    const hum2 = Number(d.hum_2);
    // Exclude invalid values by returning Infinity
    if (isNaN(hum1) || isNaN(hum2)) return Infinity;
    return Math.min(hum1, hum2);
  }).filter(value => value !== Infinity)); // Remove placeholder Infinity values
  
  let hum_data_yaxis_max = Math.max(...data.map(d => {
    const hum1 = Number(d.hum_1);
    const hum2 = Number(d.hum_2);
    // Exclude invalid values by returning -Infinity
    if (isNaN(hum1) || isNaN(hum2)) return -Infinity;
    return Math.max(hum1, hum2);
  }).filter(value => value !== -Infinity)); // Remove placeholder -Infinity values


  // min and max extrems for humidity given hpfp and hcc

  if (!isNaN(hpfp_percent) && !isNaN(hcc_percent)) {
    hum_data_yaxis_max = Math.max(hum_data_yaxis_max, hcc_percent, hpfp_percent);
    hum_data_yaxis_min = Math.min(hum_data_yaxis_min, hcc_percent, hpfp_percent);
  }

  const options = {
    chart: {
      zoomType: 'xy',
      height: '500px',
      events: {
        load: function() {
          const chart = this;
          if (window.irrigationCharts) {
            window.irrigationCharts.push(chart);
          } else {
            window.irrigationCharts = [chart];
          }
        }
      }
    },
    title: { text: 'Irrigation' },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: { day: '%e %b', hour: '%H:%M' },
      crosshair: true,
      events: {
        afterSetExtremes: function(e) {
          if (e.trigger === 'syncExtremes') return;
          
          const thisChart = this.chart;
          
          if (window.irrigationCharts) {
            window.irrigationCharts.forEach(chart => {
              if (chart !== thisChart && chart.xAxis && chart.xAxis[0]) {
                if (e.min === undefined || e.max === undefined) {
                  chart.xAxis[0].setExtremes(null, null, true, false, { trigger: 'syncExtremes' });
                } else {
                  chart.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: 'syncExtremes' });
                }
              }
            });
          }
        }
      }
    },
    yAxis: [
      {
        labels: { format: '{value}', style: { color: Highcharts.getOptions().colors[0] } },
        title: { text: 'Humidité (%)', style: { color: Highcharts.getOptions().colors[0] } },
        plotLines: [
          {
            color: '#FF0000',
            width: 2,
            value: hpfp_percent,
            dashStyle: 'shortdash',
            label: {
              text: `HPFP (${hpfp_percent}%)`,
              align: 'right',
              style: { color: '#FF0000' }
            }
          },
          {
            color: '#00FF00',
            width: 2,
            value: hcc_percent,
            dashStyle: 'shortdash',
            label: {
              text: `HCC (${hcc_percent}%)`,
              align: 'right',
              style: { color: '#00FF00' }
            }
          }
        ],
        min: hum_data_yaxis_min ? hum_data_yaxis_min : 0,
        max: hum_data_yaxis_max ? hum_data_yaxis_max : 100
      },
      {
        title: { text: 'Température (°C)', style: { color: Highcharts.getOptions().colors[2] } },
        labels: { format: '{value}', style: { color: Highcharts.getOptions().colors[2] } },
        opposite: true
      },
      {
        title: { text: 'Volume (m³)', style: { color: Highcharts.getOptions().colors[3] } },
        labels: { format: '{value}', style: { color: Highcharts.getOptions().colors[3] } },
        opposite: true
      },
      {
        title: { text: 'Pression (bar)', style: { color: Highcharts.getOptions().colors[1] } },
        labels: { format: '{value}', style: { color: Highcharts.getOptions().colors[1] } },
        opposite: true
      }
    ],
    tooltip: {
      shared: true,
      crosshairs: true,
      formatter: function () {
        const date = Highcharts.dateFormat('%e %b %Y %H:%M', this.x);
        let tooltip = `<b>${date}</b><br/>`;
        
        if (this.points && Array.isArray(this.points)) {
          this.points.forEach(point => {
            tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b><br/>`;
          });
        }
        
        return tooltip;
      }
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom'
    },
    plotOptions: {
      series: {
        connectNulls: true,
        marker: {
          enabled: false
        },
        point: {
          events: {
            mouseOver: function() {
              const chart = this.series.chart;
              const x = this.x;
              
              if (window.irrigationCharts) {
                window.irrigationCharts.forEach(otherChart => {
                  if (otherChart !== chart && otherChart.series && Array.isArray(otherChart.series)) {
                    const points = otherChart.series.map(s => {
                      return s.searchPoint({ chartX: otherChart.xAxis[0].toPixels(x) }, true);
                    }).filter(p => p);
                    
                    if (points.length > 0 && otherChart.tooltip && otherChart.xAxis[0]) {
                      otherChart.tooltip.refresh(points);
                      otherChart.xAxis[0].drawCrosshair(null, points[0]);
                    }
                  }
                });
              }
            },
            mouseOut: function() {
              if (window.irrigationCharts) {
                window.irrigationCharts.forEach(chart => {
                  if (chart.tooltip) {
                    chart.tooltip.hide();
                  }
                  if (chart.xAxis && chart.xAxis[0]) {
                    chart.xAxis[0].hideCrosshair();
                  }
                });
              }
            }
          }
        },
        events: {
          mouseOut: function() {
            if (window.irrigationCharts) {
              window.irrigationCharts.forEach(chart => {
                if (chart.tooltip) {
                  chart.tooltip.hide();
                }
                if (chart.xAxis && chart.xAxis[0]) {
                  chart.xAxis[0].hideCrosshair();
                }
              });
            }
          }
        }
      },
      spline: {
        connectNulls: true,
        marker: {
          enabled: false
        }
      }
    },
    series: [
      // Columns first (rendered behind)
      { name: 'Volume (m³)', type: 'column', yAxis: 2, data: data.map(d => [parseISODate(d.reception_date), round(d.volume, 2)]).filter(d => d[1] !== null), pointWidth: 10 },
      { name: 'Pression (bar)', type: 'column', yAxis: 3, data: data.map(d => [parseISODate(d.reception_date), round(d.p_moyenne, 2)]).filter(d => d[1] !== null), pointWidth: 10 },
      // Splines last (rendered in front)
      { name: 'Humidité 1 (%)', type: 'spline', yAxis: 0, data: data.map(d => [parseISODate(d.reception_date), round(d.hum_1, 2)]).filter(d => d[1] !== null), connectNulls: true, marker: { enabled: false }, color: '#ff0000ff' },
      { name: 'Humidité 2 (%)', type: 'spline', yAxis: 0, data: data.map(d => [parseISODate(d.reception_date), round(d.hum_2, 2)]).filter(d => d[1] !== null), connectNulls: true, marker: { enabled: false }, color: '#05b0ffff' },
      { name: 'Température (°C)', type: 'spline', yAxis: 1, data: data.map(d => [parseISODate(d.reception_date), round(d.temp_1, 2)]).filter(d => d[1] !== null), connectNulls: true, marker: { enabled: false } },
    ]
  };

  useEffect(() => {
    return () => {
      if (window.irrigationCharts && chartRef.current) {
        const chart = chartRef.current.chart;
        window.irrigationCharts = window.irrigationCharts.filter(c => c !== chart);
      }
    };
  }, []);

  return (
    <Card style={{ marginBottom: 16 }}>
      <HighchartsReact 
        highcharts={Highcharts} 
        options={options} 
        ref={chartRef}
      />
    </Card>
  );
};

export default IrrigationChart;
